import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  Box,
  Typography,
  Chip,
  Button,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add, AccountTree } from "@mui/icons-material";
import { FormSectionPane } from "../../../toolympus/components/primitives/PaneContainer";
import { TableDisplay } from "../../../toolympus/components/schemed/TableDisplay";
import { CopTreeNodeSchema } from "./useCopTreeNodes";
import {
  CopDocumentNodeLinkSchema,
  useCopDocumentNodeLinks,
} from "./useCopDocumentNodeLinks";
import { AddTreeNodeLinkDialog } from "./AddTreeNodeLinkDialog";
import { EditDocumentNodeLinkDialog } from "./EditDocumentNodeLinkDialog";

interface Props {
  decisionId: number;
  disabled?: boolean;
}

export const CopDocumentTreeNodesPanel: React.FC<Props> = ({
  decisionId,
  disabled = false,
}) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const {
    isLoading,
    allNodes,
    rootNodes,
    nodesById,
    linkedNodes,
    linkedNodeIds,
    addLink,
    editLink,
    removeLink,
  } = useCopDocumentNodeLinks(decisionId);

  const getNodePathBreadcrumb = (path?: string | null) => {
    if (!path) return "Root";
    return path
      .split(".")
      .map(id => nodesById.get(Number(id))?.title || id)
      .join(" / ");
  };

  return (
    <FormSectionPane
      titleIcon={<AccountTree fontSize="small" color="primary" />}
      title={
        <FormattedMessage
          id="cop_tree.linked_nodes"
          defaultMessage="Linked Tree Nodes"
        />
      }
      rightControls={
        <>
          {isLoading && <CircularProgress size={14} />}
          {!disabled && (
            <Button
              size="small"
              color="primary"
              startIcon={<Add />}
              onClick={() => setIsAddDialogOpen(true)}
            >
              <FormattedMessage id="common.add" defaultMessage="Add Link" />
            </Button>
          )}
        </>
      }
    >
      {/* Chips Container */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          p: 1.5,
          minHeight: 48,
          alignItems: "center",
        }}
      >
        {linkedNodes.length === 0 ? (
          !isLoading && <Typography variant="caption" color="text.secondary">
            <FormattedMessage
              id="cop_tree.no_linked_nodes"
              defaultMessage="No tree nodes linked to this document yet."
            />
          </Typography>
        ) : (
          linkedNodes.map(({ link, node }) => (
            <Tooltip
              key={link._id}
              title={
                <Box>
                  <Typography variant="caption" display="block">
                    Path: {getNodePathBreadcrumb(node.path)}
                  </Typography>
                  {link.anchor_id && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ fontStyle: "italic", mt: 0.5 }}
                    >
                      Anchor: {link.anchor_id}
                    </Typography>
                  )}
                  {!disabled && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ opacity: 0.8, mt: 0.5 }}
                    >
                      (Click to edit relation)
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Chip
                clickable={!disabled}
                onClick={disabled ? undefined : () => editLink.startEditing(link)}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {/* Relation Type Badge */}
                    {link.relation_type && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          fontWeight: 400,
                          color: "primary.main",
                          fontSize: "0.65rem",
                        }}
                      >
                        <TableDisplay
                          field="relation_type"
                          row={link}
                          schema={CopDocumentNodeLinkSchema.relation_type}
                        />
                        :
                      </Typography>
                    )}

                    {/* Color dot */}
                    {node.color && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: node.color,
                        }}
                      />
                    )}

                    {/* Title */}
                    <span>{node.title}</span>

                    {/* Node Type */}
                    {node.type && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ opacity: 0.7, fontSize: "0.65rem" }}
                      >
                        (
                        <TableDisplay
                          field="type"
                          row={node}
                          schema={CopTreeNodeSchema.type}
                        />
                        )
                      </Typography>
                    )}
                  </Box>
                }
                onDelete={disabled ? undefined : () => removeLink(link._id)}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                }}
              />
            </Tooltip>
          ))
        )}
      </Box>

      {/* Add Link Dialog */}
      <AddTreeNodeLinkDialog
        isOpen={isAddDialogOpen}
        close={() => setIsAddDialogOpen(false)}
        rootNodes={rootNodes}
        allNodes={allNodes}
        nodesById={nodesById}
        linkedNodeIds={linkedNodeIds}
        onAddLink={addLink}
      />

      {/* Edit Link Dialog */}
      <EditDocumentNodeLinkDialog
        {...editLink}
        isOpen={editLink.isEditing}
        close={editLink.cancel}
        nodesById={nodesById}
      />
    </FormSectionPane>
  );
};